import React, { useState, useEffect } from "react";
import Layout from "../../../components/layout/Layout";
import UploadCard from "../../../components/common/UploadCard";
import { Autocomplete, TextField } from "@mui/material";
import {
    useGetFileTypeViewQuery,
    useGetFileSearchViewMutation,
    useGetFileUploadTrackerMutation,
    useGetFileSyncViewMutation,
} from "../../../redux/service/modules/admin";
import Button from "../../../components/common/Button";
import Loader from "../../../components/common/loader";
import { useNotification } from "../../../context/NotificationContext";

const InputSheets = () => {
    const {
        data: fileTypeData,
        error: fileTypeError,
        isLoading: fileTypeLoading,
    } = useGetFileTypeViewQuery();
    const [selectedFileType, setSelectedFileType] = useState(null);
    const [selectedQuarter, setSelectedQuarter] = useState(null);
    const [selectedYear, setSelectedYear] = useState(null);

    const [selectedYearType, setSelectedYearType] = useState(null);
    const [isSyncing, setIsSyncing] = useState(false);
    const shouldFetch =
        selectedFileType &&
        (selectedQuarter || selectedYear) &&
        selectedYearType;
    const [isUploading, setIsUploading] = useState(false);

    const [
        getFileSearch,
        {
            data: fileSearchData,
            error: fileSearchError,
            isLoading: fileSearchLoading,
        },
    ] = useGetFileSearchViewMutation();

    useEffect(() => {
        if (shouldFetch) {
            getFileSearch({
                time_period: selectedQuarter || selectedYear,
                time_period_type: selectedYearType,
                file_type: selectedFileType,
            });
        }
    }, [
        selectedQuarter,
        selectedYearType,
        selectedYear,
        selectedFileType,
        shouldFetch,
    ]);

    const refetchFileSearch = () => {
        if (shouldFetch) {
            getFileSearch({
                time_period: selectedQuarter || selectedYear,
                time_period_type: selectedYearType,
                file_type: selectedFileType,
            });
        }
    };

    const [
        getFileSyncView,
        { data: syncData, isLoading: syncLoading, error: syncError },
    ] = useGetFileSyncViewMutation();

    const [uploadFile] = useGetFileUploadTrackerMutation();

    const generateQuarterOptions = () => {
        const quarters = [];
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth(); // 0-11

        // Determine the financial year
        // If month is April or later, current financial year is currentYear + 1
        // If month is before April, current financial year is currentYear
        const currentFinancialYear = currentMonth >= 3 ? currentYear + 1 : currentYear;

        // Generate quarters for the next 5 financial years
        for (let year = currentFinancialYear + 1; year >= currentFinancialYear - 6; year--) {
            for (let quarter = 4; quarter >= 1; quarter--) {
                quarters.push({
                    label: `Q${quarter}-${year}`,
                    value: `Q${quarter}-${year}`,
                });
            }
        }
        return quarters;
    };
    const generateYearsOptions = () => {
        const years = [];
        const currentYear = new Date().getFullYear();

        for (let year = currentYear - 5; year <= currentYear + 1; year++) {
            years.push({
                label: `FY-${year}`,
                value: `${year}`,
            });
        }
        return years;
    };

    const quarterOptions = generateQuarterOptions();
    const yearOptions = generateYearsOptions();

    const yearTypeOptions = [
        { label: "Day", value: "daily" },
        { label: "Month", value: "monthly" },
        { label: "Quarter", value: "quarterly" },
        { label: "Yearly", value: "yearly" },
    ];

    const handleFileUpdate = async (file, key) => {
        setIsUploading(true);
        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("file_type", key);
            formData.append("time_period", selectedQuarter || selectedYear);
            formData.append("time_period_type", selectedYearType);

            const result = await uploadFile(formData).unwrap();

            if (result.details) {
                try {
                    const parsedDetails = JSON.parse(result.details);

                    showNotification(
                        "success",
                        "File Uploaded",
                        "File uploaded successfully!"
                    );

                    refetchFileSearch();

                    return parsedDetails;
                } catch (e) {
                    refetchFileSearch();
                    return [];
                }
            }
        } catch (error) {
            showNotification(
                "error",
                "An Error Occurred",
                error?.data?.message ??
                    "An unexpected issue has arisen. Try again."
            );
            refetchFileSearch();

            return error.data;
        } finally {
            setIsUploading(false);
        }
    };

    const getDependencies = (key) => {
        const selected = fileTypeData?.find((fileType) => fileType.key === key);
        if (!selected) return [];
        const dependencies = selected.dependencies.map((depKey, index) => ({
            key: depKey,
            index: index,
            dependency: true,
        }));

        return [
            ...dependencies,
            { key, index: dependencies.length, dependency: false },
        ];
    };

    const renderUploadCards = () => {
        if (!selectedFileType || !shouldFetch) return null;

        const cardsToRender = getDependencies(selectedFileType);
        cardsToRender.sort((a, b) => a.index - b.index);

        const allDependenciesReady = cardsToRender
            .filter((card) => card.dependency)
            .every(({ key }) => {
                const fileData = fileSearchData?.find(
                    (file) => file.file_type === key
                );
                return (
                    fileData?.processing_status === "processed" ||
                    fileData?.processing_status === "pending"
                );
            });

        return cardsToRender.map(({ key, index, dependency }) => {
            const fileType = fileTypeData.find((ft) => ft.key === key);
            if (!fileType) return null;

            const fileData = fileSearchData?.find(
                (file) => file.file_type === fileType.key
            );
            const processingStatus = fileData?.processing_status;
            const isProcessing = processingStatus === "pending";
            const isSuccess = processingStatus === "processed";

            // Determine if this card should be disabled
            const isDisabled = !dependency && !allDependenciesReady;

            let errors = [];
            if (processingStatus === "error") {
                try {
                    const sanitizedErrorMessage =
                        fileData.error_message.replace(/NaN/g, "null");
                    const parsedErrors = JSON.parse(sanitizedErrorMessage);
                    errors = parsedErrors.filter(
                        (error) => error.table === fileType.key
                    );
                } catch (e) {}
            }

            return (
                <UploadCard
                    key={fileType.key}
                    id={fileType.key}
                    title={fileType.display_name}
                    onUpdate={async (file, key) => {
                        const errorData = await handleFileUpdate(file, key);
                        if (errorData) {
                            errors = errorData;
                        }
                    }}
                    onIgnoreAndSync={handleSyncData}
                    uploaded_at={fileData?.uploaded_at}
                    errors={errors}
                    isProcessing={isProcessing}
                    isSuccess={isSuccess}
                    areDependenciesProcessing={false}
                    responseFileName={fileData?.file_name}
                    isDisabled={isDisabled}
                />
            );
        });
    };

    const handleSyncData = async () => {
        if (!shouldFetch) {
            showNotification(
                "error",
                "Incomplete Fields",
                "Please select all required fields before syncing."
            );
            return;
        }

        setIsSyncing(true);
        try {
            const response = await getFileSyncView({
                time_period: selectedQuarter || selectedYear,
                time_period_type: selectedYearType,
                file_type: selectedFileType,
            }).unwrap();

            if (
                syncError ||
                (response?.pending && response.pending.length > 0)
            ) {
                let errorMessage = "Error syncing data.";
                if (response?.pending && response.pending.length > 0) {
                    try {
                        const firstError = response.pending[0];
                        const parsedErrors = JSON.parse(
                            firstError.error_message
                        );
                        if (parsedErrors.length > 0) {
                            errorMessage = parsedErrors[0].error_message;
                        }
                    } catch (e) {
                        console.log(e);
                    }
                }
                showNotification("error", "Error Syncing Data", errorMessage);
            } else {
                showNotification(
                    "success",
                    "Data Synced",
                    "Data synced successfully."
                );
            }
            refetchFileSearch();
        } catch (error) {
            showNotification(
                "error",
                "Error Syncing Data",
                "Error syncing data."
            );
        } finally {
            setIsSyncing(false);
        }
    };

    const isSyncButtonDisabled = () => {
        if (!shouldFetch || !fileSearchData) return true;

        // Get all cards that need to be checked
        const cardsToCheck = getDependencies(selectedFileType);

        return (
            fileSearchData.some((file) => file.processing_status === "error") || // If any error, return true
            fileSearchData.some(
                (file) => file.processing_status === "unknown"
            ) || // If any unknown, return true
            (cardsToCheck.every(({ key }) => {
                const fileData = fileSearchData.find(
                    (file) => file.file_type === key
                );
                return fileData?.processing_status === "processed";
            }) &&
                fileSearchData.every(
                    (file) => file.processing_status === "processed"
                )) || // If all processed, return true
            !fileSearchData.every(
                (file) =>
                    file.processing_status === "pending" ||
                    file.processing_status === "processed"
            ) // If not all pending or processed, return true
        );
    };

    const { showNotification } = useNotification();

    return (
        <Layout>
            {(fileTypeLoading ||
                syncLoading ||
                fileSearchLoading ||
                isUploading ||
                isSyncing) && <Loader isFullPage={true} />}
            <div className="flex flex-col gap-4 p-6 bg-white">
                {fileTypeData && (
                    <div className="max-w-md flex flex-col gap-6">
                        <div>
                            <h2 className="text-xl font-bold mb-6 text-primary">
                                Input Sheets
                            </h2>
                            <Autocomplete
                                options={fileTypeData}
                                getOptionLabel={(option) => option.display_name}
                                value={
                                    fileTypeData.find(
                                        (ft) => ft.key === selectedFileType
                                    ) || null
                                }
                                onChange={(event, newValue) => {
                                    setSelectedFileType(
                                        newValue ? newValue.key : null
                                    );
                                    if (newValue?.key === "vote_spread") {
                                        setSelectedYearType("quarterly");
                                    }

                                    if (
                                        newValue?.key === "client_interaction"
                                    ) {
                                        setSelectedYearType("daily");
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="File Type"
                                        variant="outlined"
                                    />
                                )}
                            />
                        </div>

                        {!["vote_spread", "client_interaction"].includes(
                            selectedFileType
                        ) && (
                            <Autocomplete
                                options={yearTypeOptions}
                                getOptionLabel={(option) => option.label}
                                value={
                                    yearTypeOptions.find(
                                        (ft) => ft.value === selectedYearType
                                    ) || null
                                }
                                onChange={(event, newValue) =>
                                    setSelectedYearType(
                                        newValue ? newValue.value : null
                                    )
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Financial Year Type"
                                        variant="outlined"
                                    />
                                )}
                                getOptionDisabled={(option) => {
                                    const restrictedFileTypes = [
                                        "client_revenue",
                                        "client_budget",
                                        "client_uploader",
                                        "rest_date"
                                    ];
                                    return (
                                        restrictedFileTypes.includes(selectedFileType) &&
                                        option.value === "yearly"
                                    );
                                }}
                            />
                        )}
                        {selectedYearType === "daily" && (
                            <TextField
                                label="Select Date"
                                type="date"
                                onChange={(event) =>
                                    setSelectedQuarter(event.target.value)
                                }
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        )}
                        {selectedYearType === "monthly" && (
                            <TextField
                                label="Select Month"
                                type="month"
                                onChange={(event) =>
                                    setSelectedQuarter(event.target.value)
                                }
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        )}
                        {selectedYearType === "quarterly" && (
                            <Autocomplete
                                options={quarterOptions}
                                getOptionLabel={(option) => option.label}
                                value={
                                    quarterOptions.find(
                                        (ft) => ft.value === selectedQuarter
                                    ) || null
                                }
                                onChange={(event, newValue) =>
                                    setSelectedQuarter(
                                        newValue ? newValue.value : null
                                    )
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Quarter"
                                        variant="outlined"
                                    />
                                )}
                            />
                        )}
                        {selectedYearType === "yearly" && (
                            <Autocomplete
                                options={yearOptions}
                                getOptionLabel={(option) => option.label}
                                value={
                                    yearOptions.find(
                                        (ft) => ft.value === selectedYear
                                    ) || null
                                }
                                onChange={(event, newValue) =>
                                    setSelectedYear(
                                        newValue ? newValue.value : null
                                    )
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="FY-Year"
                                        variant="outlined"
                                    />
                                )}
                            />
                        )}
                    </div>
                )}
                <div className="flex flex-wrap gap-4">
                    {renderUploadCards()}
                </div>
                <div className="flex justify-end gap-4 mt-4">
                    <Button
                        variant="primary"
                        className="max-w-lg"
                        onClick={handleSyncData}
                        disabled={isSyncing || isSyncButtonDisabled()}
                    >
                        {isSyncing ? "SYNCING..." : "SYNC DATA"}
                    </Button>
                </div>
            </div>
        </Layout>
    );
};

export default InputSheets;
